/** Small math helpers shared by client prediction and server simulation. */

export interface Vec2 {
  x: number;
  z: number;
}

export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

export const TAU = Math.PI * 2;

export function clamp(v: number, min: number, max: number): number {
  return v < min ? min : v > max ? max : v;
}

export function clamp01(v: number): number {
  return clamp(v, 0, 1);
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

export function inverseLerp(a: number, b: number, v: number): number {
  return a === b ? 0 : clamp01((v - a) / (b - a));
}

export function smoothstep(edge0: number, edge1: number, v: number): number {
  const t = inverseLerp(edge0, edge1, v);
  return t * t * (3 - 2 * t);
}

/**
 * Frame-rate independent exponential smoothing.
 * `rate` is roughly "how much of the gap is closed per second".
 */
export function damp(current: number, target: number, rate: number, dt: number): number {
  return lerp(current, target, 1 - Math.exp(-rate * dt));
}

export function dist2d(ax: number, az: number, bx: number, bz: number): number {
  const dx = ax - bx;
  const dz = az - bz;
  return Math.sqrt(dx * dx + dz * dz);
}

export function distSq2d(ax: number, az: number, bx: number, bz: number): number {
  const dx = ax - bx;
  const dz = az - bz;
  return dx * dx + dz * dz;
}

export function dist3d(a: Vec3, b: Vec3): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  const dz = a.z - b.z;
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

/** Shortest signed angular difference from `a` to `b`, in [-PI, PI]. */
export function angleDelta(a: number, b: number): number {
  let d = (b - a) % TAU;
  if (d > Math.PI) d -= TAU;
  if (d < -Math.PI) d += TAU;
  return d;
}

/** Rotate `current` toward `target` by at most `maxStep` radians. */
export function rotateToward(current: number, target: number, maxStep: number): number {
  const d = angleDelta(current, target);
  if (Math.abs(d) <= maxStep) return target;
  return current + Math.sign(d) * maxStep;
}

/**
 * Yaw whose (sin, cos) points from one place to another.
 *
 * This is the *monster's* convention: it moves along `(sin(yaw), cos(yaw))`.
 * For anything driven by a player's yaw use {@link yawToward} instead — see
 * the note on {@link forwardFromYaw}.
 */
export function headingTo(fromX: number, fromZ: number, toX: number, toZ: number): number {
  return Math.atan2(toX - fromX, toZ - fromZ);
}

/**
 * The direction a *player* faces for a given yaw.
 *
 * Three.js cameras look down -Z, so a player at yaw 0 faces -Z and their
 * forward vector is `(-sin, -cos)` — the exact negation of the monster's.
 * The two conventions coexist because each is internally consistent (the
 * camera forces one, `headingTo` plus the monster's own movement forces the
 * other), but mixing them silently rotates things by half a turn.
 *
 * That bug shipped in several places at once: the map arrow pointed backwards,
 * the compass read the opposite cardinal, thrown flares landed behind the
 * player, and — worst — the forced confrontation and the death camera both
 * turned you *away* from the monster instead of toward it.
 *
 * Anything deriving a direction from a player's yaw must go through here.
 */
export function forwardFromYaw(yaw: number): Vec2 {
  return { x: -Math.sin(yaw), z: -Math.cos(yaw) };
}

/** The player yaw that faces a given point. Inverse of {@link forwardFromYaw}. */
export function yawToward(fromX: number, fromZ: number, toX: number, toZ: number): number {
  return Math.atan2(-(toX - fromX), -(toZ - fromZ));
}

/**
 * Compass bearing in degrees for a player yaw, where north is world -Z.
 *
 * Yaw 0 faces +Z, which is due south — hence the negated cosine.
 */
export function bearingFromYaw(yaw: number): number {
  const forward = forwardFromYaw(yaw);
  const deg = (Math.atan2(forward.x, -forward.z) * 180) / Math.PI;
  return ((deg % 360) + 360) % 360;
}

export const CARDINAL_POINTS: ReadonlyArray<readonly [number, string]> = [
  [0, 'N'],
  [45, 'NE'],
  [90, 'E'],
  [135, 'SE'],
  [180, 'S'],
  [225, 'SW'],
  [270, 'W'],
  [315, 'NW'],
];

/**
 * Smallest signed difference from `b` to `a`, in [-180, 180).
 *
 * `a - b + 540` is always positive for bearings in [0, 360), so the modulo
 * needs no sign correction.
 */
export function bearingDelta(a: number, b: number): number {
  return ((a - b + 540) % 360) - 180;
}

/**
 * The cardinal label for a bearing, or null if it is not close to one.
 *
 * Worth stating because getting it backwards is easy and silent: the match is
 * on being *near* the cardinal. An earlier version tested for a difference
 * close to 180 degrees, which labelled every direction with its opposite —
 * facing north, the compass read "S".
 */
export function cardinalFor(bearingDeg: number, toleranceDeg = 2.5): string | null {
  const deg = ((bearingDeg % 360) + 360) % 360;
  for (const [at, label] of CARDINAL_POINTS) {
    if (Math.abs(bearingDelta(deg, at)) <= toleranceDeg) return label;
  }
  return null;
}

export function normalize2d(x: number, z: number): Vec2 {
  const len = Math.sqrt(x * x + z * z);
  if (len < 1e-6) return { x: 0, z: 0 };
  return { x: x / len, z: z / len };
}
